import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Wallet,
  AlertTriangle,
  ScrollText,
  Settings,
  ArrowRight,
} from "lucide-react";
import { PageHeader } from "@/components/page-header";

// Finance hub — entry point that links to the real Finance surfaces.
// The original Lovable mock here rendered hardcoded "INV-33XX" invoice
// numbers + fake vendors + a "Process Batch" CTA wired to nothing; that
// got stripped as the v1.1 Financial Close surfaces (Payment Queue,
// Exceptions Queue, Match Policy) shipped. This page now points users
// at those real surfaces instead of fabricating queue data.

export const Route = createFileRoute("/finance")({
  head: () => ({
    meta: [
      { title: "Finance & Payments — ProcureFlow" },
      {
        name: "description",
        content: "Process payments, triage exceptions, and review audit history.",
      },
    ],
  }),
  component: FinanceHub,
});

const cards = [
  {
    to: "/invoices/payment-queue",
    title: "Payment Queue",
    description: "Matched and Approved invoices ready for dispatch.",
    icon: Wallet,
  },
  {
    to: "/exceptions",
    title: "Exceptions",
    description: "Invoices that failed matching, awaiting Finance triage.",
    icon: AlertTriangle,
  },
  {
    to: "/audit-logs",
    title: "Audit Logs",
    description: "End-to-end history of procurement and payment activity.",
    icon: ScrollText,
  },
  {
    to: "/settings/match-policy",
    title: "Match Policy",
    description: "Price tolerance and add-on thresholds used by matching.",
    icon: Settings,
  },
] as const;

function FinanceHub() {
  return (
    <div className="space-y-6" data-testid="finance-hub">
      <PageHeader
        title="Finance & Payments"
        description="Pick a surface to triage exceptions, dispatch payments, or review settings."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="group rounded-2xl bg-card border border-border shadow-elegant p-5 hover:border-primary/40 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0">
                <c.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold">{c.title}</h3>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
